import { useEffect } from 'react';

import { FormState, UseFormRegister, UseFormSetValue } from 'react-hook-form';

import AvatarTitleSelection from '@event/components/AvatarTitleSelection';
import DescriptionSelection from '@event/components/DescriptionSelection';
import LocationSelection from '@event/components/LocationSelection';
import TypeEventSelection from '@event/components/TypeEventSelection';
import useRandomizedContent from '@event/hooks/useRandomizedContent';
import useEventTypesStore from '@event/store/useEventTypesStore';
import { useTutorialStore } from '@event/store/useTutorialStore';
import { CreateEvent } from '@event/types';
import Logo from '@ui/components/Logo';
import { getAssetUrl } from '@utils/getAssetUrl';
import useClientStore from '@utils/hooks/useClientStore';

import CardWrapper from './CardWrapper';

interface CardProps {
  register: UseFormRegister<CreateEvent>;
  setValue: UseFormSetValue<CreateEvent>;
  formState: FormState<CreateEvent>;
  photoId: number;
  setPhotoId: (photoId: number) => void;
}

const Card = ({
  register,
  setValue,
  formState,
  photoId,
  setPhotoId,
}: CardProps) => {
  const { selectedEventType } = useEventTypesStore();
  const { eventTitle, eventDescription } = useRandomizedContent(
    selectedEventType,
    formState.isDirty,
  );

  const tutorialStore = useClientStore(useTutorialStore, (state) => state);

  useEffect(() => {
    setValue('description', eventDescription);
    setValue('name', eventTitle);
  }, [eventDescription, eventTitle, setValue]);

  const eventImage = getAssetUrl(
    `/images/event/${selectedEventType?.name}/${photoId}.png`,
  );

  const isTutorialActive = tutorialStore?.isTutorialActive;

  const isTutorialEventType =
    tutorialStore?.tutorialStep === 'eventType' && isTutorialActive;

  const isTutorialEventTitle =
    tutorialStore?.tutorialStep === 'eventTitle' && isTutorialActive;

  const isTutorialEventDescription =
    tutorialStore?.tutorialStep === 'eventDescription' && isTutorialActive;

  const isTutorialEventLocation =
    tutorialStore?.tutorialStep === 'eventLocation' && isTutorialActive;

  return (
    <CardWrapper eventImage={eventImage}>
      <div className="relative">
        <Logo
          withGradient={false}
          formState={formState}
          className="my-6 ml-6 w-[68px] fill-brand-white-100"
        />
        <div className="relative rounded-[32px] bg-brand-white-10080 p-4 lg:rounded-[48px] lg:p-8">
          <TypeEventSelection
            formState={formState}
            setValue={setValue}
            isTutorialActive={isTutorialActive}
            isTutorialEventType={isTutorialEventType}
          />
          <AvatarTitleSelection
            register={register}
            setValue={setValue}
            formState={formState}
            photoId={photoId}
            setPhotoId={setPhotoId}
            isTutorialEventTitle={isTutorialEventTitle}
            eventImage={eventImage}
          />
          <DescriptionSelection
            register={register}
            setValue={setValue}
            formState={formState}
            isTutorialEventDescription={isTutorialEventDescription}
          />
          <LocationSelection
            register={register}
            setValue={setValue}
            formState={formState}
            isTutorialEventLocation={isTutorialEventLocation}
          />
        </div>
      </div>
    </CardWrapper>
  );
};

export default Card;
